import { useState, useEffect } from "react";
import EditSubUnitOverlay from "./EditSubUnits";

const fmt = (n) => Number(n).toLocaleString();

// Uniform brand-blue palette — cards are identical in color,
// distinguished by index number, left-border opacity steps, and avatar shade
const BASE_BLUE = { bg: "#EFF6FF", border: "#BFDBFE", track: "#DBEAFE" };
const AVATAR_SHADES = ["#1E3A8A", "#1D4ED8", "#2563EB", "#3B82F6", "#60A5FA"];

export default function StockBreakdownOverlay({ viewSubUnit, closeSubUnitView, product, currency, updateSelectedProduct, storeName }) {
  const [visible, setVisible] = useState(true);
  const [isWide, setIsWide] = useState(false);
  const [subUnitToEdit, setSubUnitToEdit] = useState(null);

  useEffect(() => {
    const check = () => setIsWide(window.innerWidth >= 600);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const closeOverlay = () => {
    setVisible(false);
    setTimeout(() => closeSubUnitView(), 360);
  };

  const totalSubQty = product.subUnits.reduce((a, s) => a + s.qty, 0);
  const totalSubInBase = product.subUnits.reduce((a, s) => a + s.qty / s.perBase, 0);
  const grandBase = product.baseQty + totalSubInBase;

  const sheetStyle = isWide
    ? {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: visible ? "translate(-50%,-50%) scale(1)" : "translate(-50%,-46%) scale(0.95)",
        opacity: visible ? 1 : 0,
        width: "min(480px, 92vw)",
        maxHeight: "88vh",
        borderRadius: 20,
        boxShadow: "0 24px 80px rgba(15,23,42,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
        transition: "transform 0.36s cubic-bezier(0.32,0.72,0,1), opacity 0.26s ease",
      }
    : {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        transform: visible ? "translateY(0)" : "translateY(100%)",
        borderRadius: "20px 20px 0 0",
        maxHeight: "91dvh",
        boxShadow: "0 -8px 40px rgba(15,23,42,0.12)",
        transition: "transform 0.38s cubic-bezier(0.32,0.72,0,1)",
      };

  return (
    <>
      <style>{`
      

        /* ── PAGE ── */
        .sbo-page {
          min-height: 100vh;
          background: #F3F6FA;
          display: flex; align-items: center; justify-content: center;
          padding: 24px 16px;
        }

        /* ── PRODUCT CARD (matches screenshot exactly) ── */
        .sbo-card {
          background: #fff;
          border-radius: 16px;
          padding: 20px 18px 18px;
          width: min(330px, 100%);
          box-shadow: 0 2px 16px rgba(15,23,42,0.07);
          display: flex; flex-direction: column; align-items: center; gap: 0;
        }
        .sbo-card-toprow {
          display: flex; justify-content: space-between; align-items: center;
          width: 100%; margin-bottom: 12px;
        }
        .sbo-icon-btn {
          width: 34px; height: 34px; border-radius: 8px;
          background: transparent; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #94a3b8; transition: background .15s;
        }
        .sbo-icon-btn:hover { background: #f1f5f9; }
        .sbo-img {
          width: 180px; height: 134px;
          background: #F1F5F9; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid #E8EDF4; margin-bottom: 14px;
        }
        .sbo-name { font-size: 16px; font-weight: 700; color: #1E293B; margin-bottom: 2px; }
        .sbo-cat  { font-size: 12px; color: #94A3B8; font-weight: 500; margin-bottom: 14px; }

        /* Stock pill — clickable */
        .sbo-stock-wrap {
          display: flex; align-items: center; gap: 12px; margin-bottom: 18px; width: 100%;
        }
        .sbo-stock-pill {
          background: #FEF9EC; border: 1.5px solid #FDE68A;
          border-radius: 12px; padding: 8px 18px;
          cursor: pointer; display: flex; flex-direction: column; align-items: center;
          transition: box-shadow .15s, transform .12s, background .15s;
          position: relative; flex: 1;
        }
        .sbo-stock-pill:hover {
          background: #FEF3C7;
          box-shadow: 0 4px 16px rgba(217,119,6,0.15);
          transform: translateY(-1px);
        }
        .sbo-stock-pill:active { transform: none; }
        .sbo-stock-num {
          font-size: 26px; font-weight: 800; color: #D97706; line-height: 1;
          letter-spacing: -0.5px;
        }
        .sbo-stock-sub { font-size: 11px; font-weight: 600; color: #92400E; margin-top: 1px; }
        .sbo-tap-hint {
          position: absolute; top: -7px; right: -7px;
          background: #1D4ED8; color: #fff;
          width: 17px; height: 17px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 9px; font-weight: 800; border: 2px solid #fff;
          animation: sb-pulse 2.2s infinite;
        }
        @keyframes sb-pulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(29,78,216,0.4); }
          55%      { box-shadow: 0 0 0 5px rgba(29,78,216,0); }
        }
        .sbo-refresh {
          width: 38px; height: 38px; border-radius: 50%;
          background: transparent; border: 1.5px solid #E2E8F0;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: #2563EB; transition: background .15s;
        }
        .sbo-refresh:hover { background: #EFF6FF; }

        
/* WHATSAPP INFO TAG */
.sbo-wa-tag {
  display: flex; align-items: center; gap: 12px;
  margin-top: 12px; padding: 13px 14px;
  border-radius: 14px;
  background: #F0FDF4; border: 1.5px solid #BBF7D0;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s, transform 0.12s;
  flex-shrink: 0;
}
.sbo-wa-tag:hover { background: #DCFCE7; border-color: #86EFAC; transform: translateY(-1px); }
.sbo-wa-tag:active { transform: none; }
.sbo-wa-icon { width: 28px; height: 28px; flex-shrink: 0; color: #16A34A; }
.sbo-wa-text { display: flex; flex-direction: column; gap: 1px; flex: 1; min-width: 0; }
.sbo-wa-hint { font-size: 10px; font-weight: 700; color: #16A34A; text-transform: uppercase; letter-spacing: 0.08em; }
.sbo-wa-text > span:not(.sbo-wa-hint):not(.sbo-wa-number) { font-size: 12px; font-weight: 600; color: #166534; }
.sbo-wa-number { font-size: 13px; font-weight: 800; color: #15803D; letter-spacing: 0.01em; margin-top: 2px; }
.sbo-wa-arrow { flex-shrink: 0; color: #16A34A; opacity: 0.7; }

        /* Product detail rows */
        .sbo-detail-section { width: 100%; }
        .sbo-detail-title { font-size: 14px; font-weight: 700; color: #1E293B; margin-bottom: 10px; }
        .sbo-detail-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 5px 0; border-bottom: 1px solid #F1F5F9;
          font-size: 13px;
        }
        .sbo-detail-row:last-child { border-bottom: none; }
        .sbo-detail-k { color: #94A3B8; font-weight: 500; }
        .sbo-detail-v { color: #1E293B; font-weight: 600; font-size: 13px; }

        /* Bottom buttons */
        .sbo-btns { display: flex; gap: 10px; width: 100%; margin-top: 16px; }
        .sbo-btn-outline {
          flex: 1; padding: 11px 0; border-radius: 10px;
          border: 1.5px solid #E2E8F0; background: #fff;
          
          color: #2563EB; cursor: pointer; transition: background .15s;
        }
        .sbo-btn-outline:hover { background: #F8FAFC; }
        .sbo-btn-filled {
          flex: 1; padding: 11px 0; border-radius: 10px;
          border: none; background: #1E3A8A;
         
        }
        .sbo-btn-filled:hover { background: #1e40af; }

        /* ── BACKDROP ── */
        .sbo-backdrop {
          position: fixed; inset: 0; z-index: 900;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
          animation: sbo-fade .2s ease;
        }
        @keyframes sbo-fade { from { opacity: 0 } to { opacity: 1 } }

        /* ── SHEET ── */
        .sbo-sheet {
          background: #fff; z-index: 950;
          overflow-y: auto;
          display: flex; flex-direction: column;
          padding: 0 20px 28px;
        }
        @media (min-width: 600px) { .sbo-sheet { padding: 0 28px 32px; } }

        .sbo-handle {
          width: 36px; height: 4px; border-radius: 2px;
          background: #E2E8F0; margin: 12px auto 18px; flex-shrink: 0;
        }

        /* Sheet header */
        .sbo-sh-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 18px;
        }
        .sbo-sh-label {
          font-size: 10px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: .12em;
        }
        .sbo-sh-title {
          font-size: clamp(18px,5vw,22px); font-weight: 800; color: #0F172A; margin-top: 2px;
          letter-spacing: -0.3px;
        }
        .sbo-sh-close {
          width: 32px; height: 32px; border-radius: 8px;
          background: #F1F5F9; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #64748B; transition: background .15s; flex-shrink: 0;
        }
        .sbo-sh-close:hover { background: #E2E8F0; color: #1E293B; }

        /* ── SUMMARY BANNER ── */
        .sbo-banner {
          background: #1E3A8A;
          border-radius: 14px;
          padding: 16px 18px;
          margin-bottom: 20px;
          display: flex; align-items: center; gap: 14px;
        }
        .sbo-banner-icon {
          width: 42px; height: 42px; border-radius: 10px;
          background: rgba(255,255,255,0.1);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .sbo-banner-label {
          font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.5);
          text-transform: uppercase; letter-spacing: .1em;
        }
        .sbo-banner-val {
          font-size: clamp(20px,5.5vw,24px); font-weight: 800; color: #fff;
          letter-spacing: -0.5px; line-height: 1.1; margin-top: 1px;
        }
        .sbo-banner-note { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 3px; }

        /* ── SECTION LABEL ── */
        .sbo-sec-label {
          font-size: 10px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: .12em;
          margin-bottom: 8px; margin-top: 2px;
        }

        /* ── BASE UNIT ROW ── */
        .sbo-base-row {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 16px;
          background: #F8FAFC; border: 1px solid #E2E8F0;
          border-radius: 12px; margin-bottom: 18px;
        }
        .sbo-base-icon {
          width: 38px; height: 38px; border-radius: 9px;
          background: #1E3A8A;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .sbo-base-name { font-size: 14px; font-weight: 700; color: #1E293B; }
        .sbo-base-meta { font-size: 11px; color: #94A3B8; margin-top: 1px; }
        .sbo-base-qty  {
          margin-left: auto;
          font-size: 20px; font-weight: 800; color: #1E293B; letter-spacing: -0.5px;
        }
        .sbo-base-unit-label {
          font-size: 11px; color: #94A3B8; font-weight: 500;
          text-align: right; margin-top: 1px;
        }

        /* ── SUB UNIT CARDS ── */
        .sbo-sub-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }

        .sbo-sub-card {
          border-radius: 12px;
          border: 1px solid #BFDBFE;
          background: #EFF6FF;
          overflow: hidden;
          position: relative;
          display: flex;
          cursor: pointer;
        }
        .sbo-sub-accent-bar {
          width: 4px; flex-shrink: 0; border-radius: 0;
        }
        .sbo-sub-inner {
          flex: 1;
          padding: 13px 15px 12px;
          display: flex; flex-direction: column; gap: 10px;
        }
        .sbo-sub-top-row {
          display: flex; align-items: center; gap: 10px;
        }
        .sbo-sub-avatar {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800; flex-shrink: 0; color: #fff;
        }
        .sbo-sub-info { flex: 1; min-width: 0; }
        .sbo-sub-name  { font-size: 14px; font-weight: 700; color: #1E3A8A; }
        .sbo-sub-ratio { font-size: 11px; margin-top: 1px; font-weight: 500; color: #2563EB; }
        .sbo-sub-qty-col { text-align: right; flex-shrink: 0; }
        .sbo-sub-qty-num { font-size: 18px; font-weight: 800; letter-spacing: -0.5px; color: #1E3A8A; }
        .sbo-sub-qty-lbl { font-size: 10px; color: #94A3B8; margin-top: 1px; }

        /* progress bar */
        .sbo-track {
          height: 5px; border-radius: 99px;
          background: #DBEAFE; overflow: hidden;
        }
        .sbo-fill {
          height: 100%; border-radius: 99px;
          transition: width .7s cubic-bezier(.4,0,.2,1);
        }

        /* two-col meta */
        .sbo-sub-meta-row {
          display: flex; justify-content: space-between; align-items: center;
        }
        .sbo-conv-tag {
          display: inline-flex; align-items: center; gap: 4px;
          border-radius: 6px; padding: 3px 9px;
          font-size: 10px; font-weight: 700;
          border: 1px solid #BFDBFE;
          background: #DBEAFE; color: #1D4ED8;
        }
        .sbo-price-note { font-size: 11px; color: #64748B; font-weight: 500; }

        /* ── CTA ── */
        .sbo-cta {
          width: 100%; padding: 14px;
          border-radius: 12px; background: #2563EB; border: none;
         
          font-size: clamp(13px,3.5vw,15px); color: #fff;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
          box-shadow: 0 4px 16px rgba(37,99,235,0.28);
          transition: background .15s, transform .1s; flex-shrink: 0;
        }
        .sbo-cta:hover { background: #1D4ED8; transform: translateY(-1px); }
        .sbo-cta:active { transform: none; }

        /* stagger animation */
        @keyframes sbo-up {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .sbo-up { animation: sbo-up .28s ease both; }
      `}</style>

      {/* ── OVERLAY ── */}
      {
        <>
          <div className='sbo-backdrop' onClick={closeOverlay} />

          <div className='sbo-sheet' style={sheetStyle}>
            <div className='sbo-handle' />

            {/* Header */}
            <div className='sbo-sh-header sbo-up' style={{ animationDelay: "0ms" }}>
              <div>
                <p className='sbo-sh-label'>Stock Breakdown</p>
                <p className='sbo-sh-title'>{product.name}</p>
              </div>
              <button className='sbo-sh-close' onClick={closeOverlay}>
                <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                  <path d='M18 6L6 18M6 6l12 12' />
                </svg>
              </button>
            </div>

            {/* Summary banner */}
            <div className='sbo-banner sbo-up' style={{ animationDelay: "40ms" }}>
              <div className='sbo-banner-icon'>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.75)' strokeWidth='2'>
                  <path d='M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z' />
                  <path d='M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' />
                </svg>
              </div>
              <div>
                <p className='sbo-banner-label'>Total stock (base equiv.)</p>
                <p className='sbo-banner-val'>
                  {fmt(Math.round(grandBase))} {product.baseUnit}
                </p>
                <p className='sbo-banner-note'>
                  {fmt(product.baseQty)} base + {fmt(Math.round(totalSubInBase))} from sub-units
                </p>
              </div>
            </div>

            {/* Base unit */}
            <p className='sbo-sec-label sbo-up' style={{ animationDelay: "70ms" }}>
              Base Unit
            </p>
            <div className='sbo-base-row sbo-up' style={{ animationDelay: "90ms" }}>
              <div className='sbo-base-icon'>
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2'>
                  <rect x='2' y='7' width='20' height='14' rx='2' />
                  <path d='M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' />
                </svg>
              </div>
              <div>
                <p className='sbo-base-name'>{product.baseUnit}</p>
                <p className='sbo-base-meta'>Primary unit</p>
              </div>
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <p className='sbo-base-qty'>{fmt(product.baseQty)}</p>
                <p className='sbo-base-unit-label'>{product.baseUnit} in stock</p>
              </div>
            </div>

            {/* Sub units */}
            {product.subUnits.length > 0 && (
              <>
                <p className='sbo-sec-label sbo-up' style={{ animationDelay: "110ms" }}>
                  Sub Units · {product.subUnits.length} configured (click to edit)
                </p>
                <div className='sbo-sub-list'>
                  {product.subUnits.map((su, i) => {
                    const avatarBg = AVATAR_SHADES[i];
                    const baseEquiv = Math.round(su.qty / su.perBase);
                    const sharePct = totalSubQty > 0 ? Math.round((su.qty / totalSubQty) * 100) : 0;

                    return (
                      <div onClick={() => setSubUnitToEdit(su)} key={su.id} className='sbo-sub-card sbo-up' style={{ animationDelay: `${130 + i * 55}ms` }}>
                        {/* numbered left accent bar */}
                        <div className='sbo-sub-accent-bar' style={{ background: avatarBg }} />
                        <div className='sbo-sub-inner'>
                          {/* Top row */}
                          <div className='sbo-sub-top-row'>
                            <div className='sbo-sub-avatar' style={{ background: avatarBg }}>
                              {i + 1}
                            </div>
                            <div className='sbo-sub-info'>
                              <p className='sbo-sub-name'>{su.name}</p>
                              <p className='sbo-sub-ratio'>
                                {su.perBase} {su.name}s = 1 {product.baseUnit}
                              </p>
                            </div>
                            <div className='sbo-sub-qty-col'>
                              <p className='sbo-sub-qty-num'>{fmt(su.qty)}</p>
                              <p className='sbo-sub-qty-lbl'>in stock</p>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div>
                            <div className='sbo-track'>
                              <div className='sbo-fill' style={{ width: `${sharePct}%`, background: avatarBg }} />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                              <p style={{ fontSize: 10, color: "#2563EB", fontWeight: 700 }}>{sharePct}% of sub-unit stock</p>
                              <p style={{ fontSize: 10, color: "#94A3B8", fontWeight: 500 }}>
                                {currency} {fmt(su.price)} / {su.name}
                              </p>
                            </div>
                          </div>

                          {/* Bottom meta */}
                          <div className='sbo-sub-meta-row'>
                            <div className='sbo-conv-tag'>
                              <svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='#2563EB' strokeWidth='2.5'>
                                <path d='M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4' />
                              </svg>
                              ≈ {fmt(baseEquiv)} {product.baseUnit}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {/* ── WhatsApp info tag ── */}
            <a
              href='https://wa.me/2348159273522'
              target='_blank'
              rel='noopener noreferrer'
              className='sbo-wa-tag sbo-up'
              style={{ animationDelay: `${170 + product.subUnits.length * 55}ms` }}
            >
              {/* WhatsApp icon */}
              <svg className='sbo-wa-icon' viewBox='0 0 24 24' fill='currentColor' xmlns='http://www.w3.org/2000/svg'>
                <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z' />
                <path d='M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.532 5.862L.057 23.428a.75.75 0 0 0 .916.916l5.566-1.475A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.713 9.713 0 0 1-4.953-1.355l-.355-.211-3.683.976.992-3.608-.232-.373A9.713 9.713 0 0 1 2.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z' />
              </svg>
              <span className='sbo-wa-text'>
                <span className='sbo-wa-hint'>Want to add more sub units?</span>
                <p className='sbo-wa-hint'>Contact StoreLense support on WhatsApp</p>
                <span className='sbo-wa-number'>+234 81 5927 3522</span>
              </span>
              {/* Arrow */}
              <svg className='sbo-wa-arrow' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                <path d='M7 17L17 7M17 7H7M17 7v10' />
              </svg>
            </a>
          </div>
        </>
      }
      {subUnitToEdit && (
        <EditSubUnitOverlay
          subUnit={subUnitToEdit}
          index={product.subUnits.findIndex((su) => su.id === subUnitToEdit.id)}
          baseUnit={product.baseUnit}
          onSave={(updatedSubUnit) => {
            // Here you would typically update the sub-unit in your state/store
            console.log("Updated sub-unit:", updatedSubUnit);
            product.subUnits[product.subUnits.findIndex((su) => su.id === updatedSubUnit.id)] = updatedSubUnit; // Update the sub-unit in the product data
            setSubUnitToEdit(null);
          }}
          onDelete={() => {
            // Here you would typically delete the sub-unit from your state/store
            console.log("Deleted sub-unit:", subUnitToEdit);
            product.subUnits = product.subUnits.filter((su) => su.id !== subUnitToEdit.id); // Remove the sub-unit from the product data
            setSubUnitToEdit(null);
          }}
          onClose={() => setSubUnitToEdit(null)}
          currency={currency}
          product={product}
          updateSelectedProduct={updateSelectedProduct}
          storeName={storeName}
        />
      )}
    </>
  );
}
